'use client'

import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Alert01Icon,
  Alert02Icon,
  ArrowDown01Icon,
  Bug01Icon,
  ZapIcon,
} from '@hugeicons-pro/core-stroke-rounded'
import { MODEL_OPTIONS } from '@/components/chat/ChatInterface'
import { LobbyHeaderTitle } from '@/contexts/LobbyHeaderMetaContext'
import { MobileHeaderTitle } from '@/contexts/MobileHeaderContext'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelect } from '@/components/ui/multi-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { CommandRunAttachments } from '@/components/automations/test-panel/CommandRunAttachments'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { AvailableCommandTool } from '@/lib/commands/available-tools-types'
import {
  addToolsButtonLabel,
  mergeToolsIntoSelection,
  toolActionFromIssue,
  toolsToAddFromAction,
} from '@/lib/commands/prompt-validation-actions'
import {
  deriveTestInputFields,
} from '@/lib/commands/test-input-schema'
import {
  type AutomationChannel,
  type CommandIconKey,
  AUTOMATION_CHANNEL_META,
} from '@/lib/automations/fallback-data'
import { CHANNEL_ICON, COMMAND_ICON } from '@/lib/automations/command-appearance'
import { EVENT_TYPE_META, isWiredEventType } from '@/lib/commands/event-types-meta'
import {
  describeCron,
  getBrowserTimezone,
  getTimezoneList,
  intervalToMinutes,
  minutesToInterval,
  type IntervalUnit,
} from '@/lib/commands/trigger-config-meta'
import { cn } from '@/lib/utils'
import { useChat } from '@/contexts/ChatContext'
import { buildCommandFormSnapshot } from '@/lib/commands/form-snapshot'
import { WRITE_SCOPED_TOOLS } from '@/lib/commands/safety'
import { CommandRecentRuns } from './CommandRecentRuns'
import { CommandAppearancePicker } from './CommandAppearancePicker'
import { AskAiChip } from './CommandCopilot/AskAiChip'
import { CopilotFooterButton } from './CommandCopilot/CopilotFooterButton'
import { PatchPreviewCard } from './CommandCopilot/PatchPreviewCard'
import { GraphBuilder } from './graph-builder/GraphBuilder'
import { ModelOptionIcon } from './ModelOptionIcon'
import { TestPanel } from './test-panel/TestPanel'

export { COMMAND_ICON } from '@/lib/automations/command-appearance'

// Form types + the pure `commandApiToFormState` transform live in a
// non-client module so server components can call them during SSR.
// Re-exported here so existing imports from '@/components/automations/CommandFormPage'
// keep working.
import {
  normalizeTriggerConfig,
  type CommandFormState,
  type PromptValidationIssue,
  type PromptValidationResult,
  type DeliveryTarget,
  type ExecutionMode,
  type TriggerType,
  type ScheduleCadence,
  blankCommandForm,
  commandApiToFormState,
} from './command-form-types'

export { blankCommandForm, commandApiToFormState }
export type { CommandFormState, ExecutionMode, TriggerType, ScheduleCadence }

// Wired events listed first (so the default trigger_type=event command
// picks one that actually fires); unwired "coming soon" events stay
// visible but disabled in the dropdown.
const eventTypes = [
  'campaign.launched',
  'campaign.paused',
  'budget.threshold_hit',
  'creative.rejected',
  'creative.approved',
  'pacing.underpacing',
  'report.weekly_ready',
] as const

function FormSection({
  title,
  caption,
  children,
}: {
  title: string
  caption: string
  children: ReactNode
}) {
  return (
    <section className="rounded-2xl border border-atrium-border bg-atrium-background text-atrium-black shadow-sm">
      <header className="border-b border-atrium-border px-6 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-atrium-muted">{title}</h2>
        <p className="mt-1 text-sm text-atrium-muted">{caption}</p>
      </header>
      <div className="space-y-4 px-6 py-5">{children}</div>
    </section>
  )
}
const presetCadences = [
  'weekday_morning',
  'weekday_evening',
  'every_hour',
  'every_15_min',
  'daily_at_noon',
  'weekly_monday',
]

function labelize(value: string) {
  return value.replace(/[._]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

function joinHumanList(values: string[]) {
  if (values.length <= 1) return values[0] ?? ''
  if (values.length === 2) return `${values[0]} and ${values[1]}`
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`
}

function splitEmailRecipients(value: string): string[] {
  return value.split(',').map((entry) => entry.trim()).filter(Boolean)
}

function isEmailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function formatCost(cents: number): string {
  return `$${(Math.max(0, cents) / 100).toFixed(2)}`
}

function serializeCommandFormState(form: CommandFormState): string {
  return JSON.stringify(form)
}

function IssueList({
  issues,
  selectedTools,
  availableToolNames,
  onAddTools,
}: {
  issues: PromptValidationIssue[]
  selectedTools: string[]
  availableToolNames: ReadonlySet<string>
  onAddTools: (tools: string[]) => void
}) {
  const selected = useMemo(() => new Set(selectedTools), [selectedTools])

  return (
    <div className="space-y-2">
      {issues.map((issue, index) => {
        const action = toolActionFromIssue(issue)
        const toolsToAdd = action ? toolsToAddFromAction(action, selected, availableToolNames) : []

        return (
          <div key={`${issue.kind}-${index}`} className="rounded-md border border-current/10 bg-atrium-background/70 p-2">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
              <span>{issue.kind.replaceAll('_', ' ')}</span>
              {issue.line_hint ? <span className="text-atrium-black/45">Line {issue.line_hint}</span> : null}
            </div>
            <p className="mt-1 text-sm">{issue.message}</p>
            {issue.excerpt ? <p className="mt-1 text-xs text-atrium-black/60">Referenced: {issue.excerpt}</p> : null}
            {issue.suggestion ? <p className="mt-1 text-xs text-atrium-black/60">Suggestion: {issue.suggestion}</p> : null}
            {toolsToAdd.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 h-7 bg-atrium-background text-xs"
                onClick={() => onAddTools(toolsToAdd)}
              >
                {addToolsButtonLabel(toolsToAdd)}
              </Button>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

function PromptValidationPanel({
  result,
  loading,
  stale,
  selectedTools,
  availableToolNames,
  onAddTools,
}: {
  result: PromptValidationResult | null
  loading: boolean
  stale: boolean
  selectedTools: string[]
  availableToolNames: ReadonlySet<string>
  onAddTools: (tools: string[]) => void
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-atrium-border bg-atrium-content px-3 py-2 text-sm text-atrium-black/70">
        <span className="size-3 animate-spin rounded-full border-2 border-atrium-black/20 border-t-atrium-black" />
        <span>Validating...</span>
      </div>
    )
  }

  if (!result) return <p className="text-xs text-atrium-black/50">Not validated yet.</p>

  if (result.health === 'ok') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">No issues found.</span>
        {stale ? <span className="rounded-full bg-atrium-content px-2 py-0.5 text-[11px] font-medium text-atrium-black/55">Prompt changed</span> : null}
      </div>
    )
  }

  if (result.health === 'warning') {
    return (
      <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-950">
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <span>{result.warnings.length} {result.warnings.length === 1 ? 'warning' : 'warnings'}</span>
          {stale ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px]">Prompt changed</span> : null}
        </div>
        <IssueList
          issues={result.warnings}
          selectedTools={selectedTools}
          availableToolNames={availableToolNames}
          onAddTools={onAddTools}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-lg border border-red-300 bg-red-50 p-3 text-red-950">
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        <span>{result.errors.length} {result.errors.length === 1 ? 'error' : 'errors'}</span>
        {stale ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px]">Prompt changed</span> : null}
      </div>
      <IssueList
        issues={result.errors}
        selectedTools={selectedTools}
        availableToolNames={availableToolNames}
        onAddTools={onAddTools}
      />
      {result.warnings.length > 0 ? (
        <div className="space-y-2 border-t border-red-200 pt-3">
          <p className="text-sm font-semibold">{result.warnings.length} {result.warnings.length === 1 ? 'warning' : 'warnings'}</p>
          <IssueList
            issues={result.warnings}
            selectedTools={selectedTools}
            availableToolNames={availableToolNames}
            onAddTools={onAddTools}
          />
        </div>
      ) : null}
    </div>
  )
}

export function CommandFormPage({
  initialCommand,
  availableTools,
  currentVersionId,
}: {
  initialCommand: CommandFormState
  availableTools: AvailableCommandTool[]
  currentVersionId?: string | null
}) {
  const router = useRouter()
  const pathname = usePathname() ?? '/commands/new'
  const {
    chatOpen,
    pendingCommandFormContext,
    proposedCommandFormPatches,
    setPendingCommandFormContext,
    setProposedCommandFormPatches,
  } = useChat()
  const [form, setForm] = useState<CommandFormState>(() => ({
    ...initialCommand,
    model_key: initialCommand.model_key ?? 'auto',
  }))
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [builderError, setBuilderError] = useState<string | null>(null)
  const [confirmDangerous, setConfirmDangerous] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [savePopoverOpen, setSavePopoverOpen] = useState(false)
  const [runPopoverOpen, setRunPopoverOpen] = useState(false)
  const [runsRefreshTrigger, setRunsRefreshTrigger] = useState(0)
  const [recipientDraft, setRecipientDraft] = useState(initialCommand.email_recipients.join(', '))
  const [alertRecipientDraft, setAlertRecipientDraft] = useState(initialCommand.alert_recipients.join(', '))
  const [savedForm, setSavedForm] = useState<CommandFormState>(() => ({
    ...initialCommand,
    model_key: initialCommand.model_key ?? 'auto',
  }))
  const [validationResult, setValidationResult] = useState<PromptValidationResult | null>(initialCommand.last_validation_result)
  const [validationPrompt, setValidationPrompt] = useState(initialCommand.last_validation_result ? initialCommand.prompt : '')
  const [validatingPrompt, setValidatingPrompt] = useState(false)
  const [pendingErrorSave, setPendingErrorSave] = useState<PromptValidationResult | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(
    initialCommand.model_key !== 'auto' || initialCommand.trigger_type === 'chat',
  )
  const [timezoneSearch, setTimezoneSearch] = useState('')

  const testFields = useMemo(() => deriveTestInputFields(form), [form])

  const pageTitle = form.id ? (form.name.trim() || 'Edit command') : 'New command'
  const channelMeta = AUTOMATION_CHANNEL_META[form.channel]
  const timezoneList = useMemo(() => getTimezoneList(), [])
  const filteredTimezones = useMemo(() => {
    const query = timezoneSearch.trim().toLowerCase()
    if (!query) return timezoneList
    return timezoneList.filter((timezone) => timezone.toLowerCase().includes(query))
  }, [timezoneList, timezoneSearch])

  const selectedWriteTools = useMemo(() => {
    return availableTools.filter((tool) => tool.writeScoped && form.allowed_tools.includes(tool.value))
  }, [availableTools, form.allowed_tools])
  const availableToolNames = useMemo(() => new Set(availableTools.map((tool) => tool.value)), [availableTools])
  const hasWriteScopedTools = useMemo(() => {
    return form.allowed_tools.some((tool) => WRITE_SCOPED_TOOLS.has(tool))
  }, [form.allowed_tools])
  const formIsDirty = useMemo(() => {
    return serializeCommandFormState(form) !== serializeCommandFormState(savedForm)
  }, [form, savedForm])
  const invalidEmailRecipients = form.email_recipients.filter((recipient) => !isEmailAddress(recipient))
  const emailDeliveryInvalid = form.delivery_target === 'chat_email' && (
    form.email_recipients.length === 0 || invalidEmailRecipients.length > 0
  )
  const invalidAlertRecipients = form.alert_recipients.filter((recipient) => !isEmailAddress(recipient))
  const alertSettingsInvalid = form.alert_on_failure && (
    form.alert_recipients.length === 0 || invalidAlertRecipients.length > 0
  )
  const rateLimitWarning = form.max_runs_per_hour > form.max_runs_per_day

  const showDangerousWarning = selectedWriteTools.length > 0 && form.trigger_type !== 'manual'
  const missingTestFields = [
    !form.name.trim() ? 'a name' : null,
    form.execution_mode === 'ai' && !form.prompt.trim() ? 'a prompt' : null,
    form.execution_mode === 'deterministic' && !!builderError ? 'a valid graph' : null,
    form.execution_mode === 'deterministic' && !form.deterministic_graph ? 'a workflow graph' : null,
    showDangerousWarning && !confirmDangerous ? 'dangerous command confirmation' : null,
    form.trigger_type === 'chat' && form.chat_invocation_hint.trim().length < 10 ? 'a chat invocation hint' : null,
    emailDeliveryInvalid ? 'valid email delivery settings' : null,
    alertSettingsInvalid ? 'valid failure alert settings' : null,
  ].filter((value): value is string => !!value)
  const saveDisabled = saving || missingTestFields.length > 0
  const loadedLegacyChat = initialCommand.chat_invocable && initialCommand.trigger_type !== 'chat'
  const validationFresh = !!validationResult && validationPrompt === form.prompt
  const effectivePromptHealth = validationFresh ? validationResult.health : form.prompt_health

  useEffect(() => {
    if (!chatOpen) return
    setPendingCommandFormContext((prev) => ({
      commandId: form.id ?? null,
      pathname,
      formState: buildCommandFormSnapshot(form),
      fullFormState: form,
      focusedField: prev?.focusedField,
    }))
  }, [chatOpen, form, pathname, setPendingCommandFormContext])

  useEffect(() => {
    return () => {
      setPendingCommandFormContext(null)
      setProposedCommandFormPatches([])
    }
  }, [setPendingCommandFormContext, setProposedCommandFormPatches])

  const patchForm = (patch: Partial<CommandFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleAddValidationTools = (tools: string[]) => {
    if (tools.length === 0) return
    const nextTools = mergeToolsIntoSelection(form.allowed_tools, tools, availableToolNames)
    if (nextTools.length === form.allowed_tools.length) return
    patchForm({ allowed_tools: nextTools })
    toast.success(
      tools.length === 1 ? `Added ${tools[0]} to tools.` : `Added ${tools.length} tools.`,
    )
  }

  const removeProposal = (id: string) => {
    setProposedCommandFormPatches((prev) => prev.filter((proposal) => proposal.id !== id))
  }

  const applyProposal = (id: string, fields: string[]) => {
    const proposal = proposedCommandFormPatches.find((candidate) => candidate.id === id)
    if (!proposal) return
    const snapshot = form
    const patch = Object.fromEntries(
      fields
        .filter((field) => field in proposal.patch)
        .map((field) => [field, proposal.patch[field as keyof CommandFormState]])
    ) as Partial<CommandFormState>
    // If the patch changes trigger_type but not trigger_config (or leaves the
    // config incoherent for the new type), seed the defaults the dropdown
    // would have used. Otherwise the server rejects with
    // "trigger_config.cadence must be cron, interval, or preset" on save.
    if (typeof patch.trigger_type === 'string') {
      const nextType = patch.trigger_type as TriggerType
      const nextConfig = (patch.trigger_config && typeof patch.trigger_config === 'object')
        ? patch.trigger_config as Record<string, unknown>
        : form.trigger_config
      patch.trigger_config = normalizeTriggerConfig(nextType, nextConfig)
    }
    patchForm(patch)
    removeProposal(id)
    toast.success(`Applied ${fields.length} ${fields.length === 1 ? 'change' : 'changes'}.`, {
      duration: 30000,
      action: {
        label: 'Undo',
        onClick: () => setForm(snapshot),
      },
    })
  }

  const scheduleRunsRefresh = () => {
    window.setTimeout(() => setRunsRefreshTrigger((value) => value + 1), 1000)
  }

  const updateTriggerConfig = (patch: Record<string, unknown>) => {
    patchForm({ trigger_config: { ...form.trigger_config, ...patch } })
  }

  const runPromptValidation = async (): Promise<PromptValidationResult | null> => {
    if (!form.prompt.trim()) return null
    setValidatingPrompt(true)
    const res = await fetch('/api/commands/validate-prompt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: form.prompt,
        selected_tools: form.allowed_tools,
        trigger_type: form.trigger_type,
        trigger_config: form.trigger_config,
        model: form.model_key ?? 'auto',
        context_attachments: form.context_attachments,
      }),
    })
    const data = await res.json().catch(() => ({}))
    setValidatingPrompt(false)
    if (!res.ok) {
      toast.error((data as { error?: string }).error || 'Could not validate prompt')
      return null
    }
    const result = data as PromptValidationResult
    setValidationResult(result)
    setValidationPrompt(form.prompt)
    patchForm({
      prompt_health: result.health,
      last_validation_result: result,
      last_validated_at: new Date().toISOString(),
    })
    return result
  }

  const saveCommand = async (options?: { navigate?: boolean; silent?: boolean; validation?: PromptValidationResult | null }): Promise<string | null> => {
    const graph = form.deterministic_graph
    if (form.execution_mode === 'deterministic' && !graph) return null
    if (form.execution_mode === 'deterministic' && builderError) return null

    setSaving(true)
    const method = form.id ? 'PATCH' : 'POST'
    const url = form.id ? `/api/commands/${form.id}` : '/api/commands'
    const chatInvocable = form.trigger_type === 'chat' ? true : initialCommand.chat_invocable
    // Belt-and-suspenders: ensure trigger_config is coherent with trigger_type at
    // save time. Same normalization the dropdown's onValueChange applies — this
    // catches proposal-apply patches and any other path that mutates the two
    // fields independently.
    const normalizedTriggerConfig = normalizeTriggerConfig(form.trigger_type, form.trigger_config)
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        trigger_config: normalizedTriggerConfig,
        chat_invocable: chatInvocable,
        deterministic_graph: graph,
        last_validation_result: options?.validation ?? (validationFresh ? validationResult : undefined),
        confirm_dangerous: showDangerousWarning ? confirmDangerous : undefined,
      }),
    })
    const data = await res.json().catch(() => ({}))
    setSaving(false)
    if (!res.ok) {
      toast.error((data as { error?: string }).error || 'Could not save command')
      return null
    }
    if (!options?.silent) toast.success('Command saved')

    const savedId = form.id || (data as { command?: { id?: string } }).command?.id || null
    const nextSavedForm = { ...form, id: savedId ?? form.id }
    if (!form.id && savedId) {
      setForm({
        ...nextSavedForm,
        ...(options?.validation ? {
          prompt_health: options.validation.health,
          last_validation_result: options.validation,
          last_validated_at: new Date().toISOString(),
        } : {}),
      })
    }
    setSavedForm(nextSavedForm)
    if (savedId && options?.navigate !== false) {
      router.replace(`/commands/${savedId}`)
    }
    return savedId
  }

  const handleSaveClick = async () => {
    if (saveDisabled || saving || validatingPrompt) return
    let result = validationFresh ? validationResult : null
    if (form.execution_mode === 'ai' && (!validationFresh || effectivePromptHealth === 'error')) {
      result = await runPromptValidation()
      if (!result) return
    }
    if (result?.errors.length) {
      setPendingErrorSave(result)
      return
    }
    await saveCommand({ validation: result })
  }

  const openTestPanel = async () => {
    const id = await saveCommand({ navigate: false, silent: true })
    if (!id) return
    setTestOpen(true)
  }

  const runCommand = async () => {
    if (!form.id || formIsDirty || running || saving) return
    setRunning(true)
    try {
      const res = await fetch(`/api/commands/${form.id}/run`, { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      if ((data as { runId?: unknown }).runId) scheduleRunsRefresh()

      if (!res.ok || (data as { status?: string }).status === 'failed') {
        toast.error((data as { error?: string }).error || 'Command failed')
        return
      }
      toast.success('Command running — check Recent Runs for output')
    } catch {
      toast.error('Command failed')
    } finally {
      setRunning(false)
    }
  }

  const handleRunClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!form.id || formIsDirty || running || saving) {
      event.preventDefault()
      setRunPopoverOpen(true)
      return
    }
    if (hasWriteScopedTools) {
      setRunPopoverOpen(true)
      return
    }
    void runCommand()
  }

  const triggerConfig = form.trigger_config
  const cadence = (triggerConfig.cadence as ScheduleCadence | undefined) ?? 'preset'
  const cronTimezone = String(triggerConfig.timezone || getBrowserTimezone())
  const cronDescription = describeCron(String(triggerConfig.expr || ''), cronTimezone)
  const interval = minutesToInterval(Number(triggerConfig.minutes || 1))
  const selectedModel = MODEL_OPTIONS.find((option) => option.value === (form.model_key ?? 'auto'))

  const canTest = !saveDisabled
  const testDisabledTooltip = missingTestFields.length > 0
    ? `Add ${joinHumanList(missingTestFields)} to enable testing`
    : null

  const breadcrumb = (
    <>
      <Link href="/commands" className="shrink-0 font-normal text-atrium-black/45 transition-colors hover:text-atrium-black">
        Commands
      </Link>
      <span className="shrink-0 font-normal text-atrium-black/30"> / </span>
      <span className="inline-flex min-w-0 items-center gap-2 truncate font-semibold">
        <span
          className="grid size-6 shrink-0 place-items-center rounded-md text-white"
          style={{ background: channelMeta.gradient }}
        >
          <HugeiconsIcon icon={COMMAND_ICON[form.icon_key]} className="size-3.5" />
        </span>
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={{ background: channelMeta.tint, color: channelMeta.ink }}
        >
          <HugeiconsIcon icon={CHANNEL_ICON[form.channel]} className="size-3" />
          {channelMeta.label}
        </span>
        <span className={cn('truncate', !form.name.trim() && 'italic text-atrium-black/45')}>
          {form.name.trim() || pageTitle}
        </span>
      </span>
    </>
  )

  return (
    <div className="bv3-canvas min-h-full">
      <LobbyHeaderTitle>{breadcrumb}</LobbyHeaderTitle>
      <MobileHeaderTitle>
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 truncate text-sm text-atrium-black">
          {breadcrumb}
        </nav>
      </MobileHeaderTitle>

      <div className="mx-auto max-w-2xl px-8 pt-12 pb-32">
        <div className="space-y-6">
              {form.id && (
                <div className="inline-flex items-center rounded-full border border-atrium-border bg-atrium-background px-3 py-1 text-sm font-medium text-atrium-black shadow-sm">
                  Lifetime cost: {formatCost(form.lifetime_cost_cents)}
                </div>
              )}

              {proposedCommandFormPatches.length > 0 && (
                <div className="space-y-4">
                  {proposedCommandFormPatches
                    .slice()
                    .sort((a, b) => b.createdAt - a.createdAt)
                    .map((proposal) => (
                      <PatchPreviewCard
                        key={proposal.id}
                        proposal={proposal}
                        currentForm={form}
                        onApply={(fields) => applyProposal(proposal.id, fields)}
                        onDiscard={() => removeProposal(proposal.id)}
                      />
                    ))}
                </div>
              )}

              <FormSection title="Identity" caption="How this command shows up in the list.">
                <div className="space-y-2">
                  <Label htmlFor="command-name">Name</Label>
                  <Input id="command-name" value={form.name} onChange={(e) => patchForm({ name: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="command-description">Description</Label>
                  <Input
                    id="command-description"
                    value={form.description}
                    placeholder="e.g., Sends a digest of any campaigns hitting >90% of their daily budget."
                    onChange={(e) => patchForm({ description: e.target.value })}
                  />
                </div>

                <CommandAppearancePicker
                  iconKey={form.icon_key}
                  channel={form.channel}
                  onIconChange={(icon_key) => patchForm({ icon_key })}
                  onChannelChange={(channel) => patchForm({ channel })}
                />
              </FormSection>

              <FormSection title="Behavior" caption="What the command does and which tools it can use.">
                <RadioGroup
                  value={form.execution_mode}
                  onValueChange={(value) => patchForm({ execution_mode: value as ExecutionMode })}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  <Label htmlFor="execution-mode-ai" className="flex cursor-pointer items-start gap-3 rounded-lg border border-atrium-border p-3 font-normal">
                    <RadioGroupItem value="ai" id="execution-mode-ai" />
                    <span>
                      <span className="block font-medium">AI</span>
                      <span className="mt-1 block text-xs leading-5 text-atrium-black/55">Describe what you want in plain English; AI decides which tools to use.</span>
                    </span>
                  </Label>
                  <Label htmlFor="execution-mode-deterministic" className="flex cursor-pointer items-start gap-3 rounded-lg border border-atrium-border p-3 font-normal">
                    <RadioGroupItem value="deterministic" id="execution-mode-deterministic" />
                    <span>
                      <span className="block font-medium">Workflow</span>
                      <span className="mt-1 block text-xs leading-5 text-atrium-black/55">Runs as a workflow (fixed step order).</span>
                    </span>
                  </Label>
                </RadioGroup>

                {form.execution_mode === 'ai' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="command-prompt" className="mb-0">Prompt</Label>
                      <AskAiChip field="prompt" seedMessage="Help me tighten this command prompt." />
                    </div>
                    <Textarea
                      id="command-prompt"
                      className="min-h-48"
                      value={form.prompt}
                      onChange={(e) => patchForm({ prompt: e.target.value })}
                    />
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={validatingPrompt || !form.prompt.trim()}
                        onClick={() => void runPromptValidation()}
                      >
                        {validatingPrompt ? 'Validating...' : 'Validate prompt'}
                      </Button>
                      <PromptValidationPanel
                        result={validationResult}
                        loading={validatingPrompt}
                        stale={!!validationResult && !validationFresh}
                        selectedTools={form.allowed_tools}
                        availableToolNames={availableToolNames}
                        onAddTools={handleAddValidationTools}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <GraphBuilder
                      value={form.deterministic_graph}
                      onChange={(graph) => patchForm({ deterministic_graph: graph })}
                      error={builderError}
                      onErrorChange={setBuilderError}
                      includeEvent={form.trigger_type === 'event'}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <div>
                    <Label className="mb-0">Context files</Label>
                    <p className="mt-1 text-xs leading-5 text-atrium-black/55">
                      CSV blocklists, reference docs, or other files the command can read on every run via read_file_attachment.
                    </p>
                  </div>
                  <CommandRunAttachments
                    attachments={form.context_attachments}
                    onChange={(context_attachments) => patchForm({ context_attachments })}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="mb-0">Tools</Label>
                    <AskAiChip field="allowed_tools" seedMessage="Looking at my prompt, which tools should this command use?" />
                  </div>
                  <MultiSelect
                    options={availableTools.map((tool) => ({
                      ...tool,
                      dotColor: tool.writeScoped ? 'red' : 'green',
                    }))}
                    selected={form.allowed_tools}
                    onSelectionChange={(allowed_tools) => patchForm({ allowed_tools })}
                    placeholder="No tools selected"
                    searchPlaceholder="Search tools"
                  />
                </div>

                <div className="rounded-lg border border-atrium-border">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium"
                    onClick={() => setAdvancedOpen((open) => !open)}
                  >
                    <span>Advanced</span>
                    <HugeiconsIcon icon={ArrowDown01Icon} className={cn('size-4 transition-transform', advancedOpen && 'rotate-180')} />
                  </button>
                  {advancedOpen && (
                    <div className="space-y-4 border-t border-atrium-border p-3">
                      <div className="space-y-2">
                        <Label>Model</Label>
                        <Select value={form.model_key ?? 'auto'} onValueChange={(value) => patchForm({ model_key: value })}>
                          <SelectTrigger className="w-full">
                            <span className="flex items-center gap-2">
                              <ModelOptionIcon model={selectedModel?.value ?? 'auto'} />
                              <span>{selectedModel?.label ?? 'Auto'}</span>
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {MODEL_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                                <span className="flex items-center gap-2">
                                  <ModelOptionIcon model={option.value} />
                                  <span>{option.label}</span>
                                  {option.comingSoon ? <span className="text-[10px] text-atrium-black/60">Soon</span> : null}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </FormSection>

              <FormSection title="When it runs" caption="How this command gets triggered.">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="mb-0">Trigger type</Label>
                    <AskAiChip field="trigger_config" seedMessage="Help me choose the right trigger for this command." />
                  </div>
                <Select
                  value={form.trigger_type}
                  onValueChange={(value) => {
                    const triggerType = value as TriggerType
                    if (triggerType === 'chat') setAdvancedOpen(true)
                    patchForm({
                      trigger_type: triggerType,
                      trigger_config: triggerType === 'schedule'
                        ? { cadence: 'preset', preset: 'weekday_morning' }
                        : triggerType === 'event'
                          ? { event_type: eventTypes[0] }
                          : {},
                    })
                    setConfirmDangerous(false)
                  }}
                >
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="schedule">Scheduled</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="chat">Chat</SelectItem>
                  </SelectContent>
                </Select>
                  {loadedLegacyChat && (
                    <p className="text-xs text-atrium-black/55">This command also runs in chat (legacy setting).</p>
                  )}
                </div>

              {form.trigger_type === 'schedule' && (
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Cadence</Label>
                    <Select
                      value={cadence}
                      onValueChange={(value) => updateTriggerConfig({ cadence: value })}
                    >
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="preset">Preset</SelectItem>
                        <SelectItem value="cron">Cron</SelectItem>
                        <SelectItem value="interval">Interval</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {cadence === 'preset' && (
                    <div className="space-y-2">
                      <Label>Preset</Label>
                      <Select
                        value={String(triggerConfig.preset || 'weekday_morning')}
                        onValueChange={(value) => updateTriggerConfig({ preset: value })}
                      >
                        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {presetCadences.map((preset) => <SelectItem key={preset} value={preset}>{labelize(preset)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {cadence === 'cron' && (
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="command-cron">Cron expression</Label>
                        <Input id="command-cron" value={String(triggerConfig.expr || '')} onChange={(e) => updateTriggerConfig({ expr: e.target.value })} />
                        {(cronDescription.readable || cronDescription.error) && (
                          <p className={cn('text-sm', cronDescription.error ? 'text-red-600' : 'text-atrium-black/55')}>
                            {cronDescription.error || cronDescription.readable}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="command-timezone">Timezone</Label>
                        <Input
                          id="command-timezone"
                          list="command-timezones"
                          value={cronTimezone}
                          onChange={(e) => {
                            setTimezoneSearch(e.target.value)
                            updateTriggerConfig({ timezone: e.target.value })
                          }}
                        />
                        <datalist id="command-timezones">
                          {filteredTimezones.slice(0, 80).map((timezone) => (
                            <option key={timezone} value={timezone} />
                          ))}
                        </datalist>
                      </div>
                    </div>
                  )}
                  {cadence === 'interval' && (
                    <div className="space-y-2">
                      <Label htmlFor="command-interval">Interval</Label>
                      <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
                        <Input
                          id="command-interval"
                          type="number"
                          min={1}
                          value={String(interval.value)}
                          onChange={(e) => updateTriggerConfig({
                            minutes: intervalToMinutes({ value: Number(e.target.value || 1), unit: interval.unit }),
                          })}
                        />
                        <Select
                          value={interval.unit}
                          onValueChange={(value) => updateTriggerConfig({
                            minutes: intervalToMinutes({ value: interval.value, unit: value as IntervalUnit }),
                          })}
                        >
                          <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minutes">Minutes</SelectItem>
                            <SelectItem value="hours">Hours</SelectItem>
                            <SelectItem value="days">Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {form.trigger_type === 'event' && (() => {
                const selectedEventType = String(triggerConfig.event_type || eventTypes[0])
                const selectedIsUnwired = !isWiredEventType(selectedEventType)
                return (
                  <div className="space-y-2">
                    <Label>Event type</Label>
                    <Select value={selectedEventType} onValueChange={(value) => updateTriggerConfig({ event_type: value })}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {eventTypes.map((eventType) => {
                          const meta = EVENT_TYPE_META[eventType]
                          const wired = meta.wired
                          // Allow the user to keep viewing an unwired value
                          // if a legacy command already saved one, but
                          // disable it for fresh selection in all other rows.
                          const isCurrentValue = eventType === selectedEventType
                          return (
                            <SelectItem
                              key={eventType}
                              value={eventType}
                              disabled={!wired && !isCurrentValue}
                              className="h-auto py-2"
                            >
                              <span className="flex w-full items-start justify-between gap-3">
                                <span className="block">
                                  <span className="block">{meta.label}</span>
                                  <code className="mt-0.5 block text-xs font-normal text-atrium-black/50">{eventType}</code>
                                </span>
                                {!wired && (
                                  <span className="shrink-0 self-center rounded-full bg-atrium-content px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-atrium-muted">
                                    Coming soon
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    {selectedIsUnwired && (
                      <Alert>
                        <AlertDescription>
                          <strong>This event type isn&apos;t live yet.</strong> The platform doesn&apos;t emit{' '}
                          <code>{selectedEventType}</code> today, so this command won&apos;t fire. Pick a different
                          trigger or wait for the producer to ship.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )
              })()}

                {form.trigger_type === 'chat' && (
                  <div className="space-y-2">
                    <Label htmlFor="chat-hint">When should chat use this? (min 10 chars)</Label>
                    <Textarea
                      id="chat-hint"
                      value={form.chat_invocation_hint}
                      onChange={(e) => patchForm({ chat_invocation_hint: e.target.value })}
                    />
                    <p className="text-xs text-atrium-black/60">{form.chat_invocation_hint.trim().length} characters</p>
                  </div>
                )}
              </FormSection>

              <FormSection title="Delivery" caption="Where successful command output is delivered.">
                <RadioGroup
                  value={form.delivery_target}
                  onValueChange={(value) => {
                    const deliveryTarget = value as DeliveryTarget
                    patchForm({
                      delivery_target: deliveryTarget,
                      email_subject: deliveryTarget === 'chat_email' && !form.email_subject.trim()
                        ? form.name
                        : form.email_subject,
                    })
                  }}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  <Label htmlFor="delivery-chat" className="flex cursor-pointer items-start gap-3 rounded-lg border border-atrium-border p-3 font-normal">
                    <RadioGroupItem value="chat" id="delivery-chat" />
                    <span>
                      <span className="block font-medium">Chat only</span>
                      <span className="mt-1 block text-xs leading-5 text-atrium-black/55">Show the result in command history and chat.</span>
                    </span>
                  </Label>
                  <Label htmlFor="delivery-chat-email" className="flex cursor-pointer items-start gap-3 rounded-lg border border-atrium-border p-3 font-normal">
                    <RadioGroupItem value="chat_email" id="delivery-chat-email" />
                    <span>
                      <span className="block font-medium">Chat + Email</span>
                      <span className="mt-1 block text-xs leading-5 text-atrium-black/55">Also email the final response after a successful run.</span>
                    </span>
                  </Label>
                </RadioGroup>

                {form.delivery_target === 'chat_email' && (
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email-recipients">Recipients</Label>
                      <Input
                        id="email-recipients"
                        value={recipientDraft}
                        placeholder="ops@example.com, team@example.com"
                        onChange={(e) => {
                          setRecipientDraft(e.target.value)
                          patchForm({ email_recipients: splitEmailRecipients(e.target.value) })
                        }}
                      />
                      {invalidEmailRecipients.length > 0 ? (
                        <p className="text-xs text-red-600">Invalid email: {invalidEmailRecipients[0]}</p>
                      ) : (
                        <p className="text-xs text-atrium-black/60">Separate multiple recipients with commas.</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email-subject">Subject</Label>
                      <Input
                        id="email-subject"
                        value={form.email_subject || form.name}
                        onChange={(e) => patchForm({ email_subject: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </FormSection>

              <FormSection title="Safety" caption="Confirmation required for automatic mutations.">
                {showDangerousWarning ? (
                  <Alert className="border-amber-500/40 bg-amber-50 text-amber-950">
                    <AlertDescription className="text-amber-950">
                      This command can mutate live data (budgets, campaigns, brand info) on an automatic trigger. Confirm you understand the impact.
                      <Label className="mt-3 flex items-start gap-3">
                        <Checkbox
                          checked={confirmDangerous}
                          onCheckedChange={(checked) => setConfirmDangerous(checked === true)}
                        />
                        <span>Yes, I understand this command will run automatically and mutate live data</span>
                      </Label>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-atrium-border p-3 text-sm text-atrium-black/55">
                    <HugeiconsIcon icon={Alert01Icon} className="mt-0.5 size-4 shrink-0" />
                    <p>No extra confirmation is required for the current trigger and tool selection.</p>
                  </div>
                )}

                <div className="rounded-lg border border-atrium-border p-3">
                  <Label className="flex items-start gap-3 font-normal">
                    <Checkbox
                      checked={form.alert_on_failure}
                      onCheckedChange={(checked) => patchForm({ alert_on_failure: checked === true })}
                    />
                    <span>
                      <span className="block font-medium">Alert me when this command fails</span>
                      <span className="mt-1 block text-xs leading-5 text-atrium-black/55">Email recipients when a real run ends in an error.</span>
                    </span>
                  </Label>

                  {form.alert_on_failure && (
                    <div className="mt-4 space-y-2">
                      <Label htmlFor="alert-recipients">Alert recipients</Label>
                      <Input
                        id="alert-recipients"
                        value={alertRecipientDraft}
                        placeholder="ops@example.com, team@example.com"
                        onChange={(e) => {
                          setAlertRecipientDraft(e.target.value)
                          patchForm({ alert_recipients: splitEmailRecipients(e.target.value) })
                        }}
                      />
                      {invalidAlertRecipients.length > 0 ? (
                        <p className="text-xs text-red-600">Invalid email: {invalidAlertRecipients[0]}</p>
                      ) : form.alert_recipients.length === 0 ? (
                        <p className="text-xs text-red-600">Add at least one recipient.</p>
                      ) : (
                        <p className="text-xs text-atrium-black/60">Separate multiple recipients with commas.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-3 rounded-lg border border-atrium-border p-3">
                  <div>
                    <h3 className="text-sm font-semibold text-atrium-black">Rate limits</h3>
                    <p className="mt-1 text-xs text-atrium-black/55">Cap how often this command can run across manual, chat, schedule, and event triggers.</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="max-runs-per-hour">Max runs per hour</Label>
                      <Input
                        id="max-runs-per-hour"
                        type="number"
                        min={1}
                        value={form.max_runs_per_hour}
                        onChange={(e) => patchForm({ max_runs_per_hour: Math.max(1, Number(e.target.value) || 1) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-runs-per-day">Max runs per day</Label>
                      <Input
                        id="max-runs-per-day"
                        type="number"
                        min={1}
                        value={form.max_runs_per_day}
                        onChange={(e) => patchForm({ max_runs_per_day: Math.max(1, Number(e.target.value) || 1) })}
                      />
                    </div>
                  </div>
                  {rateLimitWarning && (
                    <p className="text-xs text-amber-700">Max runs per hour is higher than max runs per day.</p>
                  )}
                </div>
              </FormSection>

              {form.id && currentVersionId ? (
                <CommandRecentRuns
                  commandId={form.id}
                  currentVersionId={currentVersionId}
                  refreshTrigger={runsRefreshTrigger}
                />
              ) : null}
        </div>

      </div>

      {/* Sticky action footer */}
      <div className="sticky bottom-0 z-30 mt-6 border-t border-atrium-border bg-atrium-background/95 backdrop-blur supports-[backdrop-filter]:bg-atrium-background/80">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-8 py-3">
          <div className="flex items-center gap-2">
            <CopilotFooterButton hasContent={Boolean(form.name.trim() || form.description.trim() || form.prompt.trim() || form.allowed_tools.length > 0 || form.deterministic_graph)} />

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    aria-disabled={!canTest || saving}
                    className={cn(
                      'gap-1.5',
                      (!canTest || saving) && 'cursor-not-allowed opacity-50',
                    )}
                    onClick={(e) => {
                      if (!canTest || saving) {
                        e.preventDefault()
                        return
                      }
                      void openTestPanel()
                    }}
                  >
                    <HugeiconsIcon icon={Bug01Icon} className="size-4" />
                    Test
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {!canTest
                    ? (testDisabledTooltip || 'Fix the missing fields below to enable testing.')
                    : saving
                      ? 'Saving — hold on.'
                      : 'Run this command in sandbox mode against your real workspace data. No writes will happen.'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Popover open={runPopoverOpen} onOpenChange={setRunPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  aria-disabled={!form.id || formIsDirty || running || saving}
                  className={cn('gap-1.5', (!form.id || formIsDirty || running || saving) && 'cursor-not-allowed opacity-60')}
                  onClick={handleRunClick}
                  onMouseEnter={() => (!form.id || formIsDirty || saving) && setRunPopoverOpen(true)}
                  onMouseLeave={() => (!hasWriteScopedTools || !runPopoverOpen) && setRunPopoverOpen(false)}
                  onFocus={() => (!form.id || formIsDirty || saving) && setRunPopoverOpen(true)}
                  onBlur={() => (!hasWriteScopedTools || !runPopoverOpen) && setRunPopoverOpen(false)}
                >
                  <HugeiconsIcon icon={ZapIcon} className="size-4" />
                  {running ? 'Running...' : 'Run'}
                </Button>
              </PopoverTrigger>
              {(!form.id || formIsDirty || saving) ? (
                <PopoverContent
                  align="start"
                  side="top"
                  sideOffset={8}
                  className="w-64 border-atrium-border bg-atrium-background p-3 text-atrium-black"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <p className="text-sm text-atrium-muted">Save first to run the current version</p>
                </PopoverContent>
              ) : hasWriteScopedTools ? (
                <PopoverContent
                  align="start"
                  side="top"
                  sideOffset={8}
                  className="w-80 border-atrium-border bg-atrium-background p-3 text-atrium-black"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <p className="text-sm font-semibold">Run this command for real?</p>
                  <p className="mt-1 text-sm text-atrium-muted">This will run and may modify campaigns, send emails, or write data. Continue?</p>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setRunPopoverOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setRunPopoverOpen(false)
                        void runCommand()
                      }}
                    >
                      Run
                    </Button>
                  </div>
                </PopoverContent>
              ) : null}
            </Popover>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="command-enabled-footer" className="mb-0 text-sm text-atrium-muted">
                {form.enabled ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="command-enabled-footer"
                checked={form.enabled}
                onCheckedChange={(enabled) => patchForm({ enabled })}
              />
            </div>

            <Button type="button" variant="outline" onClick={() => router.push('/commands')}>Cancel</Button>

            {/* Save — artificially disabled so the popover can render on hover.
                Shows Alert02Icon and the missing-fields checklist when blocked. */}
            <Popover open={savePopoverOpen} onOpenChange={setSavePopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  aria-disabled={saveDisabled}
                  className={cn('gap-1.5', saveDisabled && 'cursor-not-allowed opacity-60')}
                  onClick={(e) => {
                    if (saveDisabled) {
                      e.preventDefault()
                      return
                    }
                    void handleSaveClick()
                  }}
                  onMouseEnter={() => missingTestFields.length > 0 && setSavePopoverOpen(true)}
                  onMouseLeave={() => setSavePopoverOpen(false)}
                  onFocus={() => missingTestFields.length > 0 && setSavePopoverOpen(true)}
                  onBlur={() => setSavePopoverOpen(false)}
                >
                  {missingTestFields.length > 0 && <HugeiconsIcon icon={Alert02Icon} className="size-4" />}
                  {saving ? 'Saving...' : 'Save command'}
                </Button>
              </PopoverTrigger>
              {missingTestFields.length > 0 && (
                <PopoverContent
                  align="end"
                  side="top"
                  sideOffset={8}
                  className="w-72 border-atrium-border bg-atrium-background p-3 text-atrium-black"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <p className="text-sm font-medium">Can&apos;t save yet — fix these:</p>
                  <div className="mt-2 space-y-2">
                    {missingTestFields.map((field) => (
                      <Label key={field} className="flex items-center gap-2 text-sm font-normal text-atrium-muted">
                        <Checkbox checked={false} disabled />
                        <span>Add {field}</span>
                      </Label>
                    ))}
                  </div>
                </PopoverContent>
              )}
            </Popover>
          </div>
        </div>
      </div>

      <SlideOutPanel
        open={testOpen && !!form.id}
        onOpenChange={(open) => {
          if (!open) setTestOpen(false)
        }}
        className="md:max-w-xl"
      >
        {form.id ? (
          <TestPanel
            commandId={form.id}
            testFields={testFields}
            onClose={() => setTestOpen(false)}
            onRunComplete={scheduleRunsRefresh}
          />
        ) : null}
      </SlideOutPanel>

      <AlertDialog open={!!pendingErrorSave} onOpenChange={(open) => !open && setPendingErrorSave(null)}>
        <AlertDialogContent className="border-atrium-border bg-atrium-background text-atrium-black">
          <AlertDialogHeader>
            <AlertDialogTitle>Save prompt with issues?</AlertDialogTitle>
            <AlertDialogDescription className="text-atrium-black/60">
              This prompt has {pendingErrorSave?.errors.length ?? 0} {(pendingErrorSave?.errors.length ?? 0) === 1 ? 'issue' : 'issues'} that will block execution. Save anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingErrorSave(null)}>Fix first</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const result = pendingErrorSave
                setPendingErrorSave(null)
                void saveCommand({ validation: result })
              }}
            >
              Save with warnings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
